import { collection, doc, DocumentData, onSnapshot, query, Unsubscribe } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import type { UserRole } from '../../auth/AuthProvider';
import { LocationFields, managedLocations, whereLocation } from '../../auth/managedLocations';
import {
  ClientsFamily,
  ConversionFamily,
  PeriodConversion,
  SessionsFamily,
  StaffPerformanceMeta,
  StaffPerformanceRecord,
  StaffPerformanceScope,
  StaffPeriods,
  SyncCounts,
} from '../types';
import { formatDisplayName } from '../utils/metrics';

export const STAFF_PERFORMANCE_COLLECTION = 'staff_performance';
export const META_COLLECTION = 'staff_performance_meta';
export const META_DOC_ID = 'latest';
const LOG_PREFIX = '[staff-performance]';

export const NO_LOCATION_REASON = 'Your account has no assigned location, so there is no team to show.';
export const NO_STAFF_ID_REASON = 'Your account has no staff id, so no performance data can be matched to you.';
const PERMISSION_MESSAGE = "You don't have access to this staff member's performance data.";

interface ScopeMetadata extends LocationFields {
  coach_id?: string | number | null;
}

/** `users.coach_id` as the staff document id (a string), or null when the account has none. */
export function selfStaffId(metadata: ScopeMetadata | null | undefined): string | null {
  const id = metadata?.coach_id;
  if (id === null || id === undefined || id === '') return null;
  return String(id);
}

/** Which documents the signed-in user may read (spec §8.1). */
export function resolveScope(role: UserRole | null, metadata: ScopeMetadata | null | undefined): StaffPerformanceScope {
  if (role === 'ADMIN') return { kind: 'all' };
  if (role === 'MANAGER') {
    const locations = managedLocations(metadata);
    return locations.length > 0 ? { kind: 'location', locations } : { kind: 'none', reason: NO_LOCATION_REASON };
  }
  if (role === 'USER') {
    const staffId = selfStaffId(metadata);
    return staffId ? { kind: 'self', staffId } : { kind: 'none', reason: NO_STAFF_ID_REASON };
  }
  return { kind: 'none', reason: 'Sign in to see performance data.' };
}

const familyOrNull = <T>(value: unknown): T | null => (value && typeof value === 'object' ? (value as T) : null);
const stringOr = (value: unknown, fallback: string): string => (typeof value === 'string' ? value : fallback);

/** The sessions family; documents written before the Oct 2026 view change carry no `serviceCategories`. */
const sessionsOrNull = (value: unknown): SessionsFamily | null => {
  const family = familyOrNull<SessionsFamily>(value);
  if (!family) return null;
  return { ...family, serviceCategories: Array.isArray(family.serviceCategories) ? family.serviceCategories : [] };
};

/** A conversion period with `completedAllSessions`, 0 when the document predates the field. */
const withCount = (period: PeriodConversion): PeriodConversion => ({
  ...period,
  completedAllSessions: typeof period.completedAllSessions === 'number' ? period.completedAllSessions : 0,
});

/** The conversion family; documents written before the Oct 2026 view change carry no `clients` and no `completedAllSessions`. */
const conversionOrNull = (value: unknown): ConversionFamily | null => {
  const family = familyOrNull<ConversionFamily>(value);
  if (!family) return null;
  return {
    ...family,
    latest: withCount(family.latest),
    prior: withCount(family.prior),
    allTime: withCount(family.allTime),
    clients: Array.isArray(family.clients) ? family.clients : [],
  };
};

/** Firestore Timestamp (anything with toDate), Date, or null. */
export const asDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value;
  const candidate = value as { toDate?: () => Date } | null | undefined;
  if (candidate && typeof candidate.toDate === 'function') return candidate.toDate();
  return null;
};

export function mapStaffDoc(id: string, data: DocumentData): StaffPerformanceRecord {
  const periods = (data.periods ?? {}) as { latest?: unknown; prior?: unknown };
  const base: StaffPerformanceRecord = {
    id,
    staff_id: typeof data.staff_id === 'number' ? data.staff_id : Number(id),
    staff_name: stringOr(data.staff_name, ''),
    display_name: stringOr(data.display_name, ''),
    home_location: typeof data.home_location === 'string' ? data.home_location : null,
    periods: { latest: stringOr(periods.latest, ''), prior: stringOr(periods.prior, '') },
    sessions: sessionsOrNull(data.sessions),
    clients: familyOrNull<ClientsFamily>(data.clients),
    conversion: conversionOrNull(data.conversion),
    source_created_date: stringOr(data.source_created_date, ''),
    synced_at: asDate(data.synced_at),
    sync_run_id: stringOr(data.sync_run_id, ''),
  };
  return { ...base, display_name: formatDisplayName(base) };
}

const EMPTY_COUNTS: SyncCounts = { staff: 0, withClients: 0, withConversion: 0, skippedRows: 0, deleted: 0 };

const asPeriods = (value: unknown): StaffPeriods | null => {
  const candidate = value as { latest?: unknown; prior?: unknown } | null | undefined;
  return candidate && typeof candidate.latest === 'string' && typeof candidate.prior === 'string'
    ? { latest: candidate.latest, prior: candidate.prior }
    : null;
};

export function mapMetaDoc(data: DocumentData): StaffPerformanceMeta {
  const counts = (data.counts ?? {}) as Partial<SyncCounts>;
  const status: 'ok' | 'error' = data.status === 'error' ? 'error' : 'ok';
  const syncedAt = asDate(data.synced_at);
  const periods = asPeriods(data.periods);
  return {
    status,
    synced_at: syncedAt,
    // Meta documents written before these fields existed: a successful attempt is its own last success.
    last_success_at: asDate(data.last_success_at) ?? (status === 'ok' ? syncedAt : null),
    last_success_periods: asPeriods(data.last_success_periods) ?? (status === 'ok' ? periods : null),
    sync_run_id: stringOr(data.sync_run_id, ''),
    trigger: data.trigger === 'monthly' || data.trigger === 'manual' ? data.trigger : 'weekly',
    triggered_by: typeof data.triggered_by === 'string' ? data.triggered_by : null,
    duration_ms: typeof data.duration_ms === 'number' ? data.duration_ms : 0,
    source_created_date: typeof data.source_created_date === 'string' ? data.source_created_date : null,
    periods,
    counts: { ...EMPTY_COUNTS, ...counts },
    error: typeof data.error === 'string' ? data.error : null,
  };
}

/** Realtime, role-scoped list used by the team overview and the coach's own page. */
export function subscribeToStaffPerformance(
  scope: StaffPerformanceScope,
  onData: (records: StaffPerformanceRecord[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  if (scope.kind === 'none') {
    onError(new Error(scope.reason));
    return () => undefined;
  }

  const handleError = (error: Error) => {
    console.error(`${LOG_PREFIX} subscription failed`, error);
    onError(error);
  };

  if (scope.kind === 'self') {
    return onSnapshot(
      doc(db, STAFF_PERFORMANCE_COLLECTION, scope.staffId),
      (snapshot) => onData(snapshot.exists() ? [mapStaffDoc(snapshot.id, snapshot.data() ?? {})] : []),
      handleError
    );
  }

  const base = collection(db, STAFF_PERFORMANCE_COLLECTION);
  const target = scope.kind === 'location' ? query(base, whereLocation('home_location', scope.locations)) : base;
  console.info(`${LOG_PREFIX} subscribing`, scope);
  return onSnapshot(
    target,
    (snapshot) => onData(snapshot.docs.map((d) => mapStaffDoc(d.id, d.data()))),
    handleError
  );
}

/** One staff member's document (drill-in from the team view, or a coach's own page). */
export function subscribeToStaffDoc(
  staffId: string,
  onData: (record: StaffPerformanceRecord | null) => void,
  onError: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, STAFF_PERFORMANCE_COLLECTION, staffId),
    (snapshot) => onData(snapshot.exists() ? mapStaffDoc(snapshot.id, snapshot.data() ?? {}) : null),
    (error) => {
      console.error(`${LOG_PREFIX} staff doc subscription failed`, error);
      onError(error);
    }
  );
}

/** `staff_performance_meta/latest` for the "Updated" chip and the Admin Panel card. */
export function subscribeToSyncMeta(
  onData: (meta: StaffPerformanceMeta | null) => void,
  onError: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, META_COLLECTION, META_DOC_ID),
    (snapshot) => onData(snapshot.exists() ? mapMetaDoc(snapshot.data() ?? {}) : null),
    (error) => {
      console.error(`${LOG_PREFIX} meta subscription failed`, error);
      onError(error);
    }
  );
}

export function describeStaffPerformanceError(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'permission-denied') return PERMISSION_MESSAGE;
  const message = (error as { message?: string } | null)?.message;
  return message || 'Something went wrong loading performance data.';
}
